import { formatAppointmentTime } from "@zapis24/utils";

import {
  getVisibleAppointments,
  type AdminAppointmentStatus,
} from "./appointmentsView.js";

export type ScheduleCalendarAppointmentInput = {
  id: string;
  status: AdminAppointmentStatus;
  startsAt: string;
  endsAt: string;
};

export type ScheduleCalendarSlotInput = {
  startsAt: string;
  endsAt: string;
  timeStr: string;
};

export type ScheduleCalendarBusyItem<TAppointment extends ScheduleCalendarAppointmentInput> = {
  kind: "busy";
  id: string;
  startsAt: string;
  endsAt: string;
  appointment: TAppointment;
};

export type ScheduleCalendarFreeItem<TSlot extends ScheduleCalendarSlotInput> = {
  kind: "free";
  id: string;
  startsAt: string;
  endsAt: string;
  timeStr: string;
  endTimeStr: string;
  slot: TSlot;
};

export type ScheduleCalendarItem<
  TAppointment extends ScheduleCalendarAppointmentInput,
  TSlot extends ScheduleCalendarSlotInput,
> = ScheduleCalendarBusyItem<TAppointment> | ScheduleCalendarFreeItem<TSlot>;

export function buildScheduleCalendarItems<
  TAppointment extends ScheduleCalendarAppointmentInput,
  TSlot extends ScheduleCalendarSlotInput,
>({
  appointments,
  slots,
  timeZone,
}: {
  appointments: readonly TAppointment[];
  slots: readonly TSlot[];
  timeZone?: string;
}): ScheduleCalendarItem<TAppointment, TSlot>[] {
  const busyItems = getVisibleAppointments([...appointments]).map((appointment) => ({
    kind: "busy" as const,
    id: appointment.id,
    startsAt: appointment.startsAt,
    endsAt: appointment.endsAt,
    appointment,
  }));

  const freeItems = slots
    .filter((slot) => !busyItems.some((item) => intervalsOverlap(slot, item)))
    .map((slot) => ({
      kind: "free" as const,
      id: `free:${slot.startsAt}`,
      startsAt: slot.startsAt,
      endsAt: slot.endsAt,
      timeStr: slot.timeStr,
      endTimeStr: addDurationToTimeStr(slot.timeStr, getDurationMinutes(slot.startsAt, slot.endsAt)),
      slot,
    }));

  return [...busyItems, ...freeItems].sort((a, b) => {
    const startDiff = readDisplayStartMinutes(a, timeZone) - readDisplayStartMinutes(b, timeZone);
    if (startDiff !== 0) return startDiff;
    if (a.kind !== b.kind) return a.kind === "busy" ? -1 : 1;
    const endDiff = readDisplayEndMinutes(a, timeZone) - readDisplayEndMinutes(b, timeZone);
    if (endDiff !== 0) return endDiff;
    return readTime(a.startsAt) - readTime(b.startsAt);
  });
}

function intervalsOverlap(
  left: { startsAt: string; endsAt: string },
  right: { startsAt: string; endsAt: string },
): boolean {
  return readTime(left.startsAt) < readTime(right.endsAt) && readTime(left.endsAt) > readTime(right.startsAt);
}

function readTime(value: string): number {
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
}

function readDisplayStartMinutes(
  item: ScheduleCalendarItem<ScheduleCalendarAppointmentInput, ScheduleCalendarSlotInput>,
  timeZone?: string,
): number {
  if (item.kind === "free") return readTimeStrMinutes(item.timeStr);
  return readLocalClockMinutes(item.startsAt, timeZone);
}

function readDisplayEndMinutes(
  item: ScheduleCalendarItem<ScheduleCalendarAppointmentInput, ScheduleCalendarSlotInput>,
  timeZone?: string,
): number {
  if (item.kind === "free") return readTimeStrMinutes(item.endTimeStr);
  return readLocalClockMinutes(item.endsAt, timeZone);
}

function readLocalClockMinutes(value: string, timeZone?: string): number {
  return readTimeStrMinutes(formatAppointmentTime(value, timeZone));
}

function readTimeStrMinutes(value: string): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!match) return Number.POSITIVE_INFINITY;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return Number.POSITIVE_INFINITY;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return Number.POSITIVE_INFINITY;
  return hours * 60 + minutes;
}

function getDurationMinutes(startsAt: string, endsAt: string): number {
  const start = readTime(startsAt);
  const end = readTime(endsAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Math.round((end - start) / 60_000);
}

function addDurationToTimeStr(timeStr: string, durationMin: number): string {
  const [hourPart, minutePart] = timeStr.split(":");
  if (hourPart === undefined || minutePart === undefined) return timeStr;

  const hours = Number(hourPart);
  const minutes = Number(minutePart);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return timeStr;

  const total = hours * 60 + minutes + durationMin;
  const normalized = ((total % 1_440) + 1_440) % 1_440;
  const endHours = Math.floor(normalized / 60);
  const endMinutes = normalized % 60;
  return `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(2, "0")}`;
}
